import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Dossier from "@/pages/Dossier";
import AgentDossier, { AgentDossierTV } from "@/pages/AgentDossier";  // iter 31.207
import Users from "@/pages/Users";
// Iter 31.56 — legacy `import Shed from "@/pages/Shed"` removed. The
// /shredder-legacy route was deleted along with it. DriveWatch is now the
// single Shredder surface.
import DriveWatch from "@/pages/DriveWatch";
import Ledger from "@/pages/Ledger";
import Brief from "@/pages/Brief";
import Radio from "@/pages/Radio";
import Roster from "@/pages/Roster";
import AdminRoster from "@/pages/AdminRoster";  // iter 31.283 — Phase 2 Roster Admin
import AdminStations from "@/pages/AdminStations";  // iter 31.328 — Manage Stations (R2 mirror)
import AdminPrincipals from "@/pages/AdminPrincipals";  // iter 31.330 — Manage Principals (domain aliases)
import Team from "@/pages/Team";
import Patterns from "@/pages/Patterns";
import Callouts from "@/pages/Callouts";
import Cost from "@/pages/Cost";
import BuiltBy from "@/pages/BuiltBy";
import Vault from "@/pages/Vault";
import Comms from "@/pages/Comms";
import SpyFinance from "@/pages/SpyFinance";
import Cv from "@/pages/Cv";
import SpyHealth from "@/pages/SpyHealth";
import ColdReadHistory from "@/pages/ColdReadHistory";
import Billing from "@/pages/Billing";
import BillingReturn from "@/pages/BillingReturn";
import Upgrade from "@/pages/Upgrade";
import Devices from "@/pages/Devices";
import AgentFoundry from "@/pages/AgentFoundry";  // iter 31.125 — Two's foundry brief
import PhoneSentinel from "@/pages/PhoneSentinel";
import LinkedInImport from "@/pages/LinkedInImport";
import Contacts from "@/pages/Contacts";
import ShareTarget from "@/pages/ShareTarget";
import TargetSight from "@/pages/TargetSight";
import Gdpr from "@/pages/Gdpr";
import Incidents from "@/pages/Incidents";
import Home from "@/pages/Home";
import GeminiChat from "@/pages/GeminiChat";  // iter 31.308
import Handoffs from "@/pages/Handoffs";      // iter 31.321
import Manual from "@/pages/Manual";
import GmailWatch from "@/pages/GmailWatch";
import Admin from "@/pages/Admin";
import Diagnostics from "@/pages/Diagnostics";  // iter 31.142 — internal system breath
import Chamber from "@/pages/Chamber";  // iter 31.145 — Handler's Chamber
import MailPage from "@/pages/Mail";  // iter 31.168 — Agent Mail (inbound + outbound)
import RadioProvider from "@/components/RadioProvider";
import CreditWallSheet from "@/components/CreditWallSheet";
import RunawayBanner from "@/components/RunawayBanner";  // iter 31.155 — client-side circuit breaker UI
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import { GarysShedIndex, GarysShedPage } from "@/pages/GarysShed";
import { SiteIndex, SitePage, SITE_SPECS } from "@/pages/MicroSite";
import { SpyAcademyIndex, SpyAcademyPage } from "@/pages/SpyAcademy";  // iter 31.287
import SpyRadioMultiStation from "@/pages/SpyRadioMultiStation";  // iter 31.288
import SpyRadioStation from "@/pages/SpyRadioStation";  // iter 31.298 — per-station deep-link page
import RadioPlayer from "@/pages/RadioPlayer";
import { getUser } from "@/lib/api";

// Iter 31.256q — host-mode enum so masked-proxy domains slot in trivially.
// Add a new hostname here + a switch branch in App() and the new domain
// gets its own route tree without rewiring the main tree. Default 'spyfinance'
// is the canonical full app; everything else is a tight public surface.
// Iter 31.270 — replicated for the new .uk sites. garysshed.uk is a
// pure alias that reuses the existing Gary's Shed route tree.
const HOST_MODES = {
    "garys-shed.cloud": "garys-shed",
    "garysshed.uk": "garys-shed",
    "spyhealth.uk": "spyhealth",
    "spyradio.uk": "spyradio",
    "spysports.uk": "spysports",
    "spycareer.uk": "spycareer",
    "spyacademy.uk": "spyacademy",
};

function getHostMode() {
    if (typeof window === "undefined") return "spyfinance";
    return HOST_MODES[window.location.hostname] || "spyfinance";
}

function Protected({ children, role }) {
    const user = getUser();
    if (!user) return <Navigate to="/login" replace />;
    if (role && user.role !== role) return <Navigate to="/shredder" replace />;
    return children;
}

export default function App() {
    const hostMode = getHostMode();

    if (hostMode === "garys-shed") {
        return (
            <div className="App">
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<GarysShedIndex />} />
                        <Route path="/:slug" element={<GarysShedPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </div>
        );
    }

    // Iter 31.287 — spyacademy.uk has its own dedicated component tree
    // (SpyAcademyIndex / SpyAcademyPage) because its palette and layout
    // are a deliberate visual break from the shared SiteIndex renderer.
    if (hostMode === "spyacademy") {
        return (
            <div className="App">
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<SpyAcademyIndex />} />
                        <Route path="/:slug" element={<SpyAcademyPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </div>
        );
    }

    // Iter 31.270 — generic micro-site host modes. Each .uk domain
    // bootstraps a SiteIndex/SitePage tree parameterised by the matching
    // SITE_SPECS entry. New sites land by adding a row to SITE_SPECS +
    // a HOST_MODES entry above — no further wiring needed.
    if (SITE_SPECS[hostMode]) {
        const spec = SITE_SPECS[hostMode];
        return (
            <div className="App">
                <BrowserRouter>
                    <Routes>
                        {/* Iter 31.288 — spyradio.uk root gets the new
                            multi-station player instead of the generic
                            CMS index. Other .uk sites still land on
                            SiteIndex. */}
                        {spec.siteId === "spyradio" ? (
                            <Route path="/" element={<SpyRadioMultiStation />} />
                        ) : (
                            <Route path="/" element={<SiteIndex siteSpec={spec} />} />
                        )}
                        {/* Iter 31.298 — per-station deep-link on spyradio.uk.
                            Registered BEFORE /:slug so /s/<slug> resolves the
                            player, not a CMS lookup for slug='s'. */}
                        {spec.siteId === "spyradio" && (
                            <Route path="/s/:slug" element={<SpyRadioStation />} />
                        )}
                        {/* Iter 31.281 — Spy Radio native fixed-catalogue
                            player. Registered BEFORE the :slug catch-all
                            so the site running on spyradio.uk lands the
                            legacy audio surface at /player without being
                            intercepted as a CMS page slug. */}
                        {spec.siteId === "spyradio" && (
                            <Route path="/player" element={<RadioPlayer />} />
                        )}
                        <Route path="/:slug" element={<SitePage siteSpec={spec} />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </div>
        );
    }

    return (
        <div className="App">
            <BrowserRouter>
                {/* Iter 31.14 (23 May 2026) — RadioProvider sits ABOVE Routes
                    so the <audio> element + state survive every page navigation.
                    Previously, each page wrapped itself in <Shell> which owned
                    the audio ref — navigating killed playback. */}
                <RadioProvider>
                <CreditWallSheet />
                <RunawayBanner />
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Login />} />{/* Iter 31.237 — UTM-tracked signup landing */}
                    {/* Iter 28.4 (17 Feb 2026): public legal pages, no auth required.
                        LinkedIn Developer Portal / Google OAuth verification need
                        these URLs reachable without sign-in. */}
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsAndConditions />} />
                    {/* Iter 31.256m — garys-shed.cloud public surface. The CF Worker
                        on garys-shed.cloud rewrites /<slug> → /garys-shed/<slug> here. */}
                    <Route path="/garys-shed" element={<GarysShedIndex />} />
                    <Route path="/garys-shed/:slug" element={<GarysShedPage />} />
                    {/* Iter 31.270 — generic .uk micro-sites. Each CF Worker on its
                        own domain rewrites /<slug> → /<spa_prefix>/<slug> here so
                        the preview can be browsed at spyfinance.cloud/{prefix}/... */}
                    <Route path="/spyhealth" element={<SiteIndex siteSpec={SITE_SPECS.spyhealth} />} />
                    <Route path="/spyhealth/:slug" element={<SitePage siteSpec={SITE_SPECS.spyhealth} />} />
                    <Route path="/spyradio" element={<SiteIndex siteSpec={SITE_SPECS.spyradio} />} />
                    {/* Iter 31.288 — SpyRadio multi-station player preview.
                        Lives at /spyradio/live on spyfinance.cloud;
                        the same component is the ROOT of spyradio.uk. */}
                    <Route path="/spyradio/live" element={<SpyRadioMultiStation />} />
                    {/* Iter 31.298 — per-station deep-link mirror. Preview
                        of the spyradio.uk /s/<slug> surface for browsing
                        on spyfinance.cloud without needing a hosts flip. */}
                    <Route path="/spyradio/live/s/:slug" element={<SpyRadioStation />} />
                    {/* Iter 31.281 — native player. Registered BEFORE the :slug
                        catch-all below so /spyradio/player resolves the audio
                        surface, not a CMS page lookup for slug=player. */}
                    <Route path="/spyradio/player" element={<RadioPlayer />} />
                    <Route path="/spyradio/:slug" element={<SitePage siteSpec={SITE_SPECS.spyradio} />} />
                    <Route path="/spysports" element={<SiteIndex siteSpec={SITE_SPECS.spysports} />} />
                    <Route path="/spysports/:slug" element={<SitePage siteSpec={SITE_SPECS.spysports} />} />
                    <Route path="/spycareer" element={<SiteIndex siteSpec={SITE_SPECS.spycareer} />} />
                    <Route path="/spycareer/:slug" element={<SitePage siteSpec={SITE_SPECS.spycareer} />} />
                    {/* Iter 31.287 — spyacademy.uk mirror routes on spyfinance.cloud
                        for preview browsing. Dedicated component tree, distinct
                        palette + six-pillar layout. */}
                    <Route path="/spyacademy" element={<SpyAcademyIndex />} />
                    <Route path="/spyacademy/:slug" element={<SpyAcademyPage />} />
                    <Route path="/" element={<Protected><Home /></Protected>} />
                    <Route path="/chat/gemini" element={<Protected><GeminiChat /></Protected>} />{/* iter 31.308 */}
                    <Route path="/handoffs" element={<Protected><Handoffs /></Protected>} />{/* iter 31.321 */}
                    <Route path="/shredder" element={<Protected><DriveWatch /></Protected>} />
                    <Route path="/shed" element={<Navigate to="/shredder" replace />} />
                    {/* Iter 31.56 — legacy /shredder-legacy route + Shed.jsx page deleted
                        once DriveWatch was verified on production. Redirect any old
                        bookmarks to the canonical surface. */}
                    <Route path="/shredder-legacy" element={<Navigate to="/shredder" replace />} />
                    <Route path="/ledger" element={<Protected><Ledger /></Protected>} />
                    <Route path="/brief" element={<Protected><Brief /></Protected>} />
                    <Route path="/comms" element={<Protected><Comms /></Protected>} />
                    <Route path="/spy-finance" element={<Protected><SpyFinance /></Protected>} />
                    <Route path="/spy-health" element={<Protected><SpyHealth /></Protected>} />
                    <Route path="/spy-health/cold-read/history" element={<Protected><ColdReadHistory /></Protected>} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/billing/return" element={<BillingReturn />} />
                    {/* Iter 31.134 — Rule 15 recruitment poster. Public route
                        so cold visitors land before signup. CTAs route into
                        /billing where the Stripe checkout already lives. */}
                    <Route path="/upgrade" element={<Upgrade />} />
                    <Route path="/devices" element={<Protected><Devices /></Protected>} />
                    <Route path="/agents" element={<Protected><AgentFoundry /></Protected>} />
                    {/* iter 31.207 — agent dossier TV mode (auto-cycle) + per-agent */}
                    <Route path="/agent/dossier/tv" element={<Protected><AgentDossierTV /></Protected>} />
                    <Route path="/agent/:key/dossier" element={<Protected><AgentDossier /></Protected>} />
                    <Route path="/phone-sentinel" element={<Protected><PhoneSentinel /></Protected>} />
                    <Route path="/linkedin" element={<Protected><LinkedInImport /></Protected>} />
                    <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
                    <Route path="/share-target" element={<Protected><ShareTarget /></Protected>} />
                    <Route path="/target-sight" element={<Protected><TargetSight /></Protected>} />
                    <Route path="/gdpr" element={<Protected><Gdpr /></Protected>} />
                    <Route path="/incidents" element={<Protected><Incidents /></Protected>} />
                    <Route path="/admin" element={<Protected><Admin /></Protected>} />
                    <Route path="/cv" element={<Protected><Cv /></Protected>} />
                    <Route path="/patterns" element={<Protected><Patterns /></Protected>} />
                    <Route path="/callouts" element={<Protected><Callouts /></Protected>} />
                    <Route path="/cost" element={<Protected><Cost /></Protected>} />
                    <Route path="/team" element={<Protected><Team /></Protected>} />
                    {/* iter 31.25 — Roster and Built By merged into /team. Old
                        URLs preserved as redirects so deep links still work. */}
                    <Route path="/roster" element={<Navigate to="/team" replace />} />
                    <Route path="/built-by" element={<Navigate to="/team" replace />} />
                    {/* Keep the underlying components reachable for any embed/print
                        flow that hits them directly via a non-Protected wrapper —
                        but the canonical app surface is /team. */}
                    <Route path="/roster-legacy" element={<Protected><Roster /></Protected>} />
                    {/* iter 31.283 (Phase 2, 8 Jul 2026) — Roster admin surface.
                        Internal-only: search/filter/tag/promote over
                        db.roster_entries. Foundation for the future
                        spycareer.uk public Operators page. */}
                    <Route path="/admin/roster" element={<Protected role="internal"><AdminRoster /></Protected>} />
                    {/* iter 31.328 — Manage Stations: on/off toggle, Drive→R2 sync, health. */}
                    <Route path="/admin/stations" element={<Protected role="internal"><AdminStations /></Protected>} />
                    {/* iter 31.330 — Manage Principals: identity binding + domain aliases. */}
                    <Route path="/admin/principals" element={<Protected role="internal"><AdminPrincipals /></Protected>} />
                    <Route path="/built-by-legacy" element={<Protected><BuiltBy /></Protected>} />
                    <Route path="/vault" element={<Protected><Vault /></Protected>} />
                    <Route path="/dossiers" element={<Protected><Dashboard /></Protected>} />
                    <Route path="/dossiers/:id" element={<Protected><Dossier /></Protected>} />
                    <Route path="/users" element={<Protected role="internal"><Users /></Protected>} />
                    <Route path="/diagnostics" element={<Protected role="internal"><Diagnostics /></Protected>} />
                    <Route path="/chamber" element={<Protected role="internal"><Chamber /></Protected>} />
                    <Route path="/mail" element={<Protected role="internal"><MailPage /></Protected>} />
                    <Route path="/radio" element={<Protected><Radio /></Protected>} />
                    {/* Iter 31.317 — per-station pages use the shared player
                        engine (RadioPlayer). It reads the slug from URL
                        params and looks up per-station identity
                        (name / voice / palette) from `STATION_IDENTITIES`
                        in the same file. One engine, many identities. */}
                    <Route path="/radio/:slug" element={<RadioPlayer />} />
                    {/* `/player` remains the default-identity engine
                        (fallback to legacy SPY_RADIO_PUBLIC when no slug
                        is present) and also serves `/player/<slug>` for
                        deep-links that don't hit /radio/<slug>. */}
                    <Route path="/player" element={<RadioPlayer />} />
                    <Route path="/player/:slug" element={<RadioPlayer />} />
                    <Route path="/manual" element={<Protected><Manual /></Protected>} />
                    <Route path="/gmail-watch" element={<Protected><GmailWatch /></Protected>} />
                    {/* Iter 31.7 (21 May 2026) — Academy / Shop / Pig Pen stubs
                        retired. The drawer was too crowded; nothing real shipped
                        in those slots. They can come back as real surfaces
                        when there's content to put in them. */}
                    <Route path="/academy" element={<Navigate to="/brief" replace />} />
                    <Route path="/shop" element={<Navigate to="/brief" replace />} />
                    <Route path="/pig-pen" element={<Navigate to="/brief" replace />} />
                    <Route path="*" element={<Navigate to="/shredder" replace />} />
                </Routes>
                </RadioProvider>
            </BrowserRouter>
        </div>
    );
}
